import { ArrowsClockwiseIcon, ChecksIcon, CreditCardIcon, EyeIcon, PaperPlaneTiltIcon, PencilSimpleIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import { Group } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { IOrderPlus } from "../../models/Order"
import { DeliveryRowAction } from "../delivery/action-controls"

/** Statuses the server lets the sender edit (de-order-service routes/api/order/update.ts). */
export const EDITABLE_ORDER_STATUSES = ["DRAFT", "REGISTERED", "REVIEW_REJECTED"]

interface OrderRowActionsProps {
  order: IOrderPlus
  onDetail: () => void
  onReorder: () => void
  onEdit: () => void
  onReview: () => void
  onApprove: () => void
  onReject: () => void
  onPayment: () => void
  onCancel: () => void
  permissions: {
    canDetail: boolean
    canEdit: boolean
    canCancel: boolean
    canReorder: boolean
  }
  currentUserId?: string
  reordering?: boolean
}

/**
 * Canvas OrdB_Orders: view, re-order and cancel on every row (cancel aria-disabled with
 * its reason when the server would refuse it). Edit, review, approve/reject and pay
 * are workflow steps: shown only on the rows they belong to.
 */
export function OrderRowActions({
  order,
  onDetail,
  onReorder,
  onEdit,
  onReview,
  onApprove,
  onReject,
  onPayment,
  onCancel,
  permissions,
  currentUserId,
  reordering,
}: OrderRowActionsProps) {
  const { t } = useTranslation(["order", "common"])
  const status = order.orderStatus || ""
  // The buyer list (logics/order/list-buyer.ts) returns PURCHASE orders this business
  // sent and SALES orders it received, so the type answers «mine» when the session
  // carries no business id (/me may return currentBusinessId null).
  const isSender = currentUserId ? currentUserId === order.senderBusiness?.id : order.type === "PURCHASE"
  const isReceiver = currentUserId ? currentUserId === order.receiverBusiness?.id : order.type === "SALES"
  const hasProformaInvoice = !!order.proformaInvoiceId
  const awaitingAdvance = status === "APPROVED" && hasProformaInvoice
  // The proforma invoice (invoiceId === proformaInvoiceId) carries the payment state.
  const isPaid = order.invoice?.paymentStatus === "CLOSED" || order.invoice?.invoiceStatus === "CLOSED"

  const denied = (code: string) => ({ allowed: false, reason: t("common:permission.required", { code }) })
  const ok = { allowed: true, reason: null }

  // de-order-service logics/order/cancel.ts: only the sender, only while REVIEWED.
  const cancelState = !permissions.canCancel
    ? denied("ORD_CANCEL")
    : isSender && status === "REVIEWED"
      ? ok
      : { allowed: false, reason: t("order:buyerOrders.cancelBlocked") }

  return (
    <Group gap={6} wrap="nowrap">
      <DeliveryRowAction
        label={t("order:actions.detail")}
        icon={<EyeIcon />}
        state={permissions.canDetail ? ok : denied("ORD_SO_DETAIL")}
        onClick={onDetail}
      />
      <DeliveryRowAction
        label={t("order:buyerOrders.reorder")}
        icon={<ArrowsClockwiseIcon />}
        state={permissions.canReorder ? ok : denied("ORD_SAVE / ORD_SEND")}
        loading={reordering}
        onClick={onReorder}
      />
      <DeliveryRowAction label={t("common:actions.cancel")} icon={<XIcon />} state={cancelState} onClick={onCancel} />

      {isSender && EDITABLE_ORDER_STATUSES.includes(status) && (
        <DeliveryRowAction
          label={t("order:actions.edit")}
          icon={<PencilSimpleIcon />}
          state={permissions.canEdit ? ok : denied("ORD_SAVE / ORD_SEND")}
          onClick={onEdit}
        />
      )}

      {status === "REGISTERED" && isSender && (
        <DeliveryRowAction label={t("order:actions.review")} icon={<PaperPlaneTiltIcon />} onClick={onReview} />
      )}

      {status === "REVIEWED" && isReceiver && (
        <>
          <DeliveryRowAction label={t("order:actions.approve")} icon={<ChecksIcon />} onClick={onApprove} />
          <DeliveryRowAction label={t("order:actions.reject")} icon={<WarningIcon />} onClick={onReject} />
        </>
      )}

      {hasProformaInvoice && (
        <DeliveryRowAction
          label={isPaid ? t("order:actions.paid") : awaitingAdvance ? t("order:actions.payAdvance") : t("order:actions.payment")}
          icon={<CreditCardIcon />}
          state={isPaid ? { allowed: false, reason: t("order:actions.paid") } : ok}
          onClick={onPayment}
        />
      )}
    </Group>
  )
}
